import React, {
    useEffect,
    useRef,
    useState,
} from "react";
import axios from "axios";

const GET_SAS_KEY =
    "jsf-hethCN8FzrSQoxHTT-dG8Vi-w_PAISdCVcqrbIBxAzFujElZOg==";

const UPDATE_IMAGE_KEY =
    "6EXpNfBWlTrTM2IJEN9VQX4FFSvjfpvkYeaAFKaSmUr9AzFuCYKgFQ==";

const GET_SAS_API =
    "https://oppiesfunc.azurewebsites.net/api/getShopImageUploadUrl";

const UPDATE_IMAGE_API =
    "https://oppiesfunc.azurewebsites.net/api/updateShopImageRecord";

const MAX_FILE_SIZE =
    10 * 1024 * 1024;

export default function ShopImageTab({
    shopId,
    userEmail,
}) {

    const authenticatedEmail =
        (
            userEmail ||
            localStorage.getItem(
                "shopEmail"
            ) ||
            ""
        )
            .trim()
            .toLowerCase();

    const fileInputRef = useRef(null);

    const [file, setFile] =
        useState(null);

    const [preview, setPreview] =
        useState("");

    const [imageUrl, setImageUrl] =
        useState("");

    const [uploading, setUploading] =
        useState(false);

    const [dragging, setDragging] =
        useState(false);

    const [status, setStatus] =
        useState({
            type: "",
            message: "",
        });

    /*
     * =========================================================
     * CLEAN UP LOCAL PREVIEW
     * =========================================================
     */

    useEffect(() => {
        return () => {
            if (
                preview &&
                preview.startsWith(
                    "blob:"
                )
            ) {
                URL.revokeObjectURL(
                    preview
                );
            }
        };
    }, [preview]);

    /*
     * =========================================================
     * STATUS
     * =========================================================
     */

    const showStatus = (
        type,
        message
    ) => {
        setStatus({
            type,
            message,
        });
    };

    /*
     * =========================================================
     * RESET LOCAL FILE
     * =========================================================
     */

    const clearLocalFile = () => {
        if (
            preview &&
            preview.startsWith(
                "blob:"
            )
        ) {
            URL.revokeObjectURL(
                preview
            );
        }

        setFile(null);

        if (
            preview.startsWith(
                "blob:"
            )
        ) {
            setPreview("");
        }

        if (
            fileInputRef.current
        ) {
            fileInputRef.current.value =
                "";
        }
    };

    /*
     * =========================================================
     * VALIDATE / APPLY FILE
     * =========================================================
     */

    const applyFile = (
        selectedFile
    ) => {
        if (!selectedFile) {
            return;
        }

        if (
            !selectedFile.type ||
            !selectedFile.type.startsWith(
                "image/"
            )
        ) {
            showStatus(
                "error",
                "Please choose a valid image file."
            );

            return;
        }

        if (
            selectedFile.size >
            MAX_FILE_SIZE
        ) {
            showStatus(
                "error",
                "Your image must be smaller than 10 MB."
            );

            return;
        }

        if (
            preview &&
            preview.startsWith(
                "blob:"
            )
        ) {
            URL.revokeObjectURL(
                preview
            );
        }

        const objectUrl =
            URL.createObjectURL(
                selectedFile
            );

        setFile(selectedFile);

        setPreview(objectUrl);

        /*
         * Local image becomes the active
         * source, so remove URL mode.
         */

        setImageUrl("");

        setStatus({
            type: "",
            message: "",
        });
    };

    /*
     * =========================================================
     * FILE INPUT
     * =========================================================
     */

    const handleFileChange = (
        event
    ) => {
        const selected =
            event.target.files?.[0];

        applyFile(selected);
    };

    /*
     * =========================================================
     * DRAG / DROP
     * =========================================================
     */

    const handleDragOver = (
        event
    ) => {
        event.preventDefault();

        if (!uploading) {
            setDragging(true);
        }
    };

    const handleDragLeave = (
        event
    ) => {
        event.preventDefault();

        setDragging(false);
    };

    const handleDrop = (
        event
    ) => {
        event.preventDefault();

        setDragging(false);

        if (uploading) {
            return;
        }

        const droppedFile =
            event.dataTransfer
                ?.files?.[0];

        applyFile(droppedFile);
    };

    /*
     * =========================================================
     * URL
     * =========================================================
     */

    const handleUrlChange = (
        event
    ) => {
        const value =
            event.target.value;

        setImageUrl(value);

        /*
         * URL becomes active source.
         */

        if (file) {
            clearLocalFile();
        }

        setPreview(
            value.trim()
        );

        if (status.message) {
            setStatus({
                type: "",
                message: "",
            });
        }
    };

    /*
     * =========================================================
     * URL VALIDATION
     * =========================================================
     */

    const validateImageUrl = (
        value
    ) => {
        try {
            const url =
                new URL(value);

            return (
                url.protocol ===
                "https:" ||
                url.protocol ===
                "http:"
            );
        } catch {
            return false;
        }
    };

    /*
     * =========================================================
     * SAVE IMAGE RECORD
     * =========================================================
     */

    const saveImageRecord =
        async (
            finalImageUrl
        ) => {

            await axios.post(
                `${UPDATE_IMAGE_API}?code=${UPDATE_IMAGE_KEY}`,
                {
                    /*
                     * Branch currently selected
                     * in Dashboard.
                     */
                    shopId,

                    imageUrl:
                        finalImageUrl,

                    /*
                     * Actual Portal account.
                     */
                    userEmail:
                        authenticatedEmail,
                }
            );
        };

    /*
     * =========================================================
     * UPLOAD LOCAL IMAGE
     * =========================================================
     */

    const uploadLocalImage =
        async () => {
            /*
             * STEP 1
             * Get SAS upload URL.
             */

            const { data } =
                await axios.get(
                    GET_SAS_API,
                    {
                        params: {
                            shopId,

                            userEmail:
                                authenticatedEmail,

                            code:
                                GET_SAS_KEY,
                        },
                    }
                );

            const sasUrl =
                data?.uploadUrl;

            if (!sasUrl) {
                throw new Error(
                    "Upload URL was not returned."
                );
            }

            /*
             * STEP 2
             * Upload directly to
             * Azure Blob Storage.
             */

            await axios.put(
                sasUrl,
                file,
                {
                    headers: {
                        "x-ms-blob-type":
                            "BlockBlob",

                        "Content-Type":
                            file.type,
                    },
                }
            );

            /*
             * Remove SAS token before
             * storing public image URL.
             */

            const finalImageUrl =
                sasUrl.split("?")[0];

            /*
             * STEP 3
             * Save/update image record.
             */

            await saveImageRecord(
                finalImageUrl
            );

            return finalImageUrl;
        };

    /*
     * =========================================================
     * SAVE
     * =========================================================
     */

    const handleSave =
        async () => {
            if (uploading) {
                return;
            }

            if (!shopId) {

                showStatus(
                    "error",
                    "Your shop ID is unavailable. Please sign in again."
                );

                return;
            }

            if (!authenticatedEmail) {

                showStatus(
                    "error",
                    "Your account email is unavailable. Please sign in again."
                );

                return;
            }

            const cleanUrl =
                imageUrl.trim();

            /*
             * Nothing selected.
             */

            if (
                !file &&
                !cleanUrl
            ) {
                showStatus(
                    "error",
                    "Choose an image from your device or enter an image URL."
                );

                return;
            }

            /*
             * URL validation.
             */

            if (
                !file &&
                cleanUrl &&
                !validateImageUrl(
                    cleanUrl
                )
            ) {
                showStatus(
                    "error",
                    "Please enter a valid image URL beginning with https:// or http://."
                );

                return;
            }

            try {
                setUploading(true);

                if (file) {
                    showStatus(
                        "info",
                        "Uploading your shop image..."
                    );

                    const finalUrl =
                        await uploadLocalImage();

                    setPreview(
                        finalUrl
                    );

                    /*
                     * Local blob URL is no
                     * longer needed.
                     */

                    setFile(null);

                    if (
                        fileInputRef.current
                    ) {
                        fileInputRef.current.value =
                            "";
                    }

                    showStatus(
                        "success",
                        "Shop image uploaded and saved successfully."
                    );

                } else {

                    /*
                     * External URL mode.
                     */

                    showStatus(
                        "info",
                        "Saving your shop image..."
                    );

                    await saveImageRecord(
                        cleanUrl
                    );

                    setPreview(
                        cleanUrl
                    );

                    setImageUrl(
                        cleanUrl
                    );

                    showStatus(
                        "success",
                        "Shop image URL saved successfully."
                    );
                }

            } catch (err) {

                console.error(
                    "❌ Shop image error:",
                    err?.response?.data ||
                    err?.message ||
                    err
                );

                if (
                    err?.response
                        ?.status ===
                    403
                ) {

                    showStatus(
                        "error",
                        err?.response
                            ?.data
                            ?.error ||
                        "You are not authorised to manage this shop."
                    );

                    return;
                }

                showStatus(
                    "error",
                    err?.response
                        ?.data
                        ?.error ||
                    "We couldn't save your shop image. Please try again."
                );

            } finally {

                setUploading(false);
            }
        };

    /*
     * =========================================================
     * CLEAR
     * =========================================================
     */

    const clearSelection = () => {

        if (
            preview &&
            preview.startsWith(
                "blob:"
            )
        ) {
            URL.revokeObjectURL(
                preview
            );
        }

        setFile(null);

        setPreview("");

        setImageUrl("");

        setStatus({
            type: "",
            message: "",
        });

        if (
            fileInputRef.current
        ) {
            fileInputRef.current.value =
                "";
        }
    };

    /*
     * =========================================================
     * RENDER
     * =========================================================
     */

    return (
        <>
            <style>
                {shopImageStyles}
            </style>

            <div className="shop-image-page">

                {/* ==============================================
                    INTRO
                ============================================== */}

                <section className="shop-image-intro">

                    <div className="shop-image-eyebrow">
                        Shop profile
                    </div>

                    <h1>
                        Shop Image
                    </h1>

                    <p>
                        Upload the main image shown
                        on your OPPIES shop profile.
                        A clear, welcoming photo helps
                        shoppers recognise your store
                        before they arrive.
                    </p>

                </section>

                {/* ==============================================
                    STATUS
                ============================================== */}

                {status.message ? (
                    <div
                        className={
                            `shop-image-status ${status.type
                                ? `shop-image-status-${status.type}`
                                : ""
                            }`
                        }
                    >

                        <div className="shop-image-status-icon">

                            {status.type ===
                                "success"
                                ? "✓"
                                : status.type ===
                                    "error"
                                    ? "!"
                                    : "i"}

                        </div>

                        <span>
                            {status.message}
                        </span>

                    </div>
                ) : null}

                {/* ==============================================
                    MAIN GRID
                ============================================== */}

                <div className="shop-image-layout">

                    {/* ==========================================
                        UPLOAD PANEL
                    ========================================== */}

                    <section className="shop-image-panel">

                        <div className="shop-image-panel-header">

                            <div>

                                <div className="shop-image-panel-eyebrow">
                                    Image source
                                </div>

                                <h2>
                                    Choose your image
                                </h2>

                                <p>
                                    Upload an image from
                                    your device or paste
                                    an existing image URL.
                                </p>

                            </div>

                            <div className="shop-image-header-icon">

                                <svg
                                    viewBox="0 0 24 24"
                                    width="20"
                                    height="20"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M12 3v12" />
                                    <path d="m7 8 5-5 5 5" />
                                    <path d="M5 14v5h14v-5" />
                                </svg>

                            </div>

                        </div>

                        {/* ======================================
                            DEVICE UPLOAD
                        ====================================== */}

                        <div className="shop-image-section-label">
                            Upload from device
                        </div>

                        <label
                            className={
                                `shop-image-dropzone ${dragging
                                    ? "shop-image-dropzone-active"
                                    : ""
                                } ${file
                                    ? "shop-image-dropzone-selected"
                                    : ""
                                }`
                            }
                            onDragOver={
                                handleDragOver
                            }
                            onDragLeave={
                                handleDragLeave
                            }
                            onDrop={
                                handleDrop
                            }
                        >

                            <input
                                ref={
                                    fileInputRef
                                }
                                type="file"
                                accept="image/*"
                                className="shop-image-hidden-input"
                                onChange={
                                    handleFileChange
                                }
                                disabled={
                                    uploading
                                }
                            />

                            <div className="shop-image-upload-icon">

                                {file ? (
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="21"
                                        height="21"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="m5 12 4 4L19 6" />
                                    </svg>
                                ) : (
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="21"
                                        height="21"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M12 3v12" />
                                        <path d="m7 8 5-5 5 5" />
                                        <path d="M5 14v5h14v-5" />
                                    </svg>
                                )}

                            </div>

                            <strong>
                                {file
                                    ? file.name
                                    : "Choose an image or drag it here"}
                            </strong>

                            <span>
                                JPG, PNG, WEBP or
                                another standard image
                                format
                            </span>

                            <small>
                                Maximum file size:
                                10 MB
                            </small>

                        </label>

                        {/* ======================================
                            DIVIDER
                        ====================================== */}

                        <div className="shop-image-divider">
                            OR USE AN IMAGE URL
                        </div>

                        {/* ======================================
                            URL
                        ====================================== */}

                        <div className="shop-image-section-label">
                            Image URL
                        </div>

                        <div className="shop-image-url-field">

                            <div className="shop-image-url-icon">

                                <svg
                                    viewBox="0 0 24 24"
                                    width="16"
                                    height="16"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                                </svg>

                            </div>

                            <input
                                type="url"
                                value={
                                    imageUrl
                                }
                                onChange={
                                    handleUrlChange
                                }
                                placeholder="https://example.com/shop-image.jpg"
                                disabled={
                                    uploading
                                }
                            />

                        </div>

                        <p className="shop-image-url-note">
                            Use a publicly accessible
                            URL so OPPIES shoppers can
                            see the image.
                        </p>

                    </section>

                    {/* ==============================================
                        PREVIEW
                    ============================================== */}

                    <section className="shop-image-preview-panel">

                        <div className="shop-image-preview-header">

                            <div>

                                <div className="shop-image-panel-eyebrow">
                                    Preview
                                </div>

                                <h2>
                                    Shop profile image
                                </h2>

                            </div>

                            {preview ? (
                                <button
                                    type="button"
                                    className="shop-image-clear-button"
                                    onClick={
                                        clearSelection
                                    }
                                    disabled={
                                        uploading
                                    }
                                >
                                    Clear
                                </button>
                            ) : null}

                        </div>

                        <div className="shop-image-preview-frame">

                            {preview ? (
                                <img
                                    src={preview}
                                    alt="Shop preview"
                                    onError={() => {
                                        if (
                                            imageUrl
                                        ) {
                                            showStatus(
                                                "error",
                                                "We couldn't load that image URL. Check that the image is publicly accessible."
                                            );
                                        }
                                    }}
                                />
                            ) : (
                                <div className="shop-image-empty">

                                    <div className="shop-image-empty-icon">

                                        <svg
                                            viewBox="0 0 24 24"
                                            width="28"
                                            height="28"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <rect
                                                x="3"
                                                y="3"
                                                width="18"
                                                height="18"
                                                rx="3"
                                            />

                                            <circle
                                                cx="8.5"
                                                cy="8.5"
                                                r="1.5"
                                            />

                                            <path d="m21 15-5-5L5 21" />
                                        </svg>

                                    </div>

                                    <strong>
                                        No image selected
                                    </strong>

                                    <span>
                                        Your selected shop
                                        image will appear
                                        here.
                                    </span>

                                </div>
                            )}

                        </div>

                        <div className="shop-image-tip">

                            <div className="shop-image-tip-icon">

                                <svg
                                    viewBox="0 0 24 24"
                                    width="16"
                                    height="16"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <circle
                                        cx="12"
                                        cy="12"
                                        r="9"
                                    />

                                    <path d="M12 11v5" />

                                    <path d="M12 8h.01" />
                                </svg>

                            </div>

                            <div>

                                <strong>
                                    Best results
                                </strong>

                                <span>
                                    Use a bright,
                                    landscape-style photo
                                    showing your shop
                                    clearly. Avoid images
                                    containing lots of
                                    small text.
                                </span>

                            </div>

                        </div>

                    </section>

                </div>

                {/* ================================================
                    ACTION BAR
                ================================================ */}

                <section className="shop-image-actions">

                    <div className="shop-image-action-copy">

                        <strong>
                            Ready to update your
                            image?
                        </strong>

                        <span>
                            Saving will replace the
                            image currently associated
                            with this shop.
                        </span>

                    </div>

                    <button
                        type="button"
                        className="shop-image-save-button"
                        onClick={
                            handleSave
                        }
                        disabled={
                            uploading ||
                            (!file &&
                                !imageUrl.trim())
                        }
                    >

                        {uploading ? (
                            <>
                                <span className="shop-image-button-spinner" />

                                Saving...
                            </>
                        ) : (
                            <>
                                <svg
                                    viewBox="0 0 24 24"
                                    width="16"
                                    height="16"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.9"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" />

                                    <path d="M17 21v-8H7v8" />

                                    <path d="M7 3v5h8" />
                                </svg>

                                Save Shop Image
                            </>
                        )}

                    </button>

                </section>

            </div>
        </>
    );
}

const shopImageStyles = `

  .shop-image-page {
    color: #1f2937;
    font-family:
      Inter,
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      sans-serif;
  }

  /* ========================================================
     INTRO
  ======================================================== */

  .shop-image-intro {
    margin-bottom: 26px;
  }

  .shop-image-eyebrow,
  .shop-image-panel-eyebrow {
    color: #047857;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }

  .shop-image-intro h1 {
    max-width: 650px;
    margin: 7px 0;
    color: #17211c;
    font-size:
      clamp(
        26px,
        3vw,
        36px
      );
    line-height: 1.08;
    letter-spacing: -0.035em;
  }

  .shop-image-intro p {
    max-width: 690px;
    margin: 0;
    color: #64748b;
    font-size: 14px;
    line-height: 1.65;
  }

  /* ========================================================
     STATUS
  ======================================================== */

  .shop-image-status {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 18px;
    padding: 12px 14px;
    border: 1px solid #e5e7eb;
    border-radius: 13px;
    background: #ffffff;
    color: #475569;
    font-size: 12px;
    font-weight: 600;
  }

  .shop-image-status-icon {
    display: grid;
    place-items: center;
    width: 25px;
    height: 25px;
    flex: 0 0 auto;
    border-radius: 50%;
    background: #f1f5f9;
    font-weight: 900;
  }

  .shop-image-status-success {
    border-color: #bbf7d0;
    background: #f0fdf4;
    color: #047857;
  }

  .shop-image-status-success
  .shop-image-status-icon {
    background: #dcfce7;
  }

  .shop-image-status-error {
    border-color: #fecaca;
    background: #fef2f2;
    color: #b91c1c;
  }

  .shop-image-status-error
  .shop-image-status-icon {
    background: #fee2e2;
  }

  .shop-image-status-info {
    border-color: #d1fae5;
    background: #f0fdf4;
    color: #047857;
  }

  /* ========================================================
     GRID
  ======================================================== */

  .shop-image-layout {
    display: grid;
    grid-template-columns:
      minmax(0, 1.05fr)
      minmax(300px, 0.95fr);
    gap: 18px;
  }

  .shop-image-panel,
  .shop-image-preview-panel {
    padding: 22px;
    border: 1px solid #e5e7eb;
    border-radius: 20px;
    background: #ffffff;
    box-shadow:
      0 8px 24px
      rgba(
        15,
        23,
        42,
        0.035
      );
  }

  /* ========================================================
     PANEL HEADER
  ======================================================== */

  .shop-image-panel-header,
  .shop-image-preview-header {
    display: flex;
    align-items: flex-start;
    justify-content:
      space-between;
    gap: 15px;
    margin-bottom: 20px;
  }

  .shop-image-panel-header h2,
  .shop-image-preview-header h2 {
    margin: 4px 0 5px;
    color: #17211c;
    font-size: 20px;
    line-height: 1.2;
    letter-spacing: -0.025em;
  }

  .shop-image-panel-header p {
    margin: 0;
    color: #64748b;
    font-size: 12px;
    line-height: 1.55;
  }

  .shop-image-header-icon {
    display: grid;
    place-items: center;
    width: 42px;
    height: 42px;
    flex: 0 0 auto;
    border-radius: 13px;
    background: #ecfdf5;
    color: #047857;
  }

  /* ========================================================
     SECTION LABEL
  ======================================================== */

  .shop-image-section-label {
    margin-bottom: 8px;
    color: #475569;
    font-size: 10px;
    font-weight: 800;
  }

  /* ========================================================
     DROPZONE
  ======================================================== */

  .shop-image-dropzone {
    display: flex;
    min-height: 165px;
    box-sizing: border-box;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px;
    border: 1.5px dashed #cbd5d0;
    border-radius: 15px;
    background: #fbfdfc;
    cursor: pointer;
    text-align: center;
    transition:
      border-color 0.18s ease,
      background 0.18s ease,
      transform 0.18s ease;
  }

  .shop-image-dropzone:hover {
    border-color: #6bb99d;
    background: #f6fcf8;
  }

  .shop-image-dropzone-active {
    border-color: #047857;
    background: #ecfdf5;
    transform: scale(1.005);
  }

  .shop-image-dropzone-selected {
    border-color: #8fcdb7;
    background: #f0fdf4;
  }

  .shop-image-hidden-input {
    display: none;
  }

  .shop-image-upload-icon {
    display: grid;
    place-items: center;
    width: 43px;
    height: 43px;
    margin-bottom: 10px;
    border-radius: 13px;
    background: #ecfdf5;
    color: #047857;
  }

  .shop-image-dropzone-selected
  .shop-image-upload-icon {
    background: #047857;
    color: #ffffff;
  }

  .shop-image-dropzone strong {
    max-width: 100%;
    overflow: hidden;
    color: #334155;
    font-size: 11px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .shop-image-dropzone span {
    margin-top: 4px;
    color: #64748b;
    font-size: 9px;
  }

  .shop-image-dropzone small {
    margin-top: 5px;
    color: #94a3b8;
    font-size: 8px;
  }

  /* ========================================================
     DIVIDER
  ======================================================== */

  .shop-image-divider {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 17px 0;
    color: #a1a9a5;
    font-size: 8px;
    font-weight: 800;
    letter-spacing: 0.1em;
  }

  .shop-image-divider::before,
  .shop-image-divider::after {
    content: "";
    height: 1px;
    flex: 1;
    background: #ecefeb;
  }

  /* ========================================================
     URL
  ======================================================== */

  .shop-image-url-field {
    display: flex;
    align-items: center;
    overflow: hidden;
    border: 1px solid #d8ddd9;
    border-radius: 11px;
    background: #ffffff;
    transition:
      border-color 0.18s ease,
      box-shadow 0.18s ease;
  }

  .shop-image-url-field:focus-within {
    border-color: #34a27a;
    box-shadow:
      0 0 0 3px
      rgba(
        4,
        120,
        87,
        0.08
      );
  }

  .shop-image-url-icon {
    display: grid;
    width: 40px;
    flex: 0 0 auto;
    place-items: center;
    color: #94a3b8;
  }

  .shop-image-url-field input {
    width: 100%;
    min-width: 0;
    min-height: 42px;
    box-sizing: border-box;
    padding: 9px 11px 9px 0;
    border: none;
    outline: none;
    background: transparent;
    color: #1f2937;
    font-family: inherit;
    font-size: 10px;
  }

  .shop-image-url-field input::placeholder {
    color: #b0b8b4;
  }

  .shop-image-url-note {
    margin: 7px 2px 0;
    color: #94a3b8;
    font-size: 8px;
    line-height: 1.5;
  }

  /* ========================================================
     PREVIEW
  ======================================================== */

  .shop-image-clear-button {
    padding: 6px 9px;
    border: 1px solid #d8ddd9;
    border-radius: 8px;
    background: #ffffff;
    color: #64748b;
    cursor: pointer;
    font-family: inherit;
    font-size: 8px;
    font-weight: 800;
  }

  .shop-image-clear-button:hover:not(:disabled) {
    background: #f8faf9;
  }

  .shop-image-preview-frame {
    display: flex;
    min-height: 300px;
    overflow: hidden;
    align-items: center;
    justify-content: center;
    border: 1px solid #e4e9e6;
    border-radius: 15px;
    background: #f7f8f6;
  }

  .shop-image-preview-frame img {
    display: block;
    width: 100%;
    height: 300px;
    object-fit: cover;
  }

  .shop-image-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 30px;
    text-align: center;
  }

  .shop-image-empty-icon {
    display: grid;
    place-items: center;
    width: 55px;
    height: 55px;
    margin-bottom: 12px;
    border-radius: 16px;
    background: #eef2ef;
    color: #94a3b8;
  }

  .shop-image-empty strong {
    color: #64748b;
    font-size: 11px;
  }

  .shop-image-empty span {
    max-width: 210px;
    margin-top: 4px;
    color: #94a3b8;
    font-size: 9px;
    line-height: 1.5;
  }

  /* ========================================================
     TIP
  ======================================================== */

  .shop-image-tip {
    display: flex;
    gap: 10px;
    margin-top: 14px;
    padding: 12px 13px;
    border-radius: 12px;
    background: #fbf7ed;
  }

  .shop-image-tip-icon {
    display: grid;
    place-items: center;
    width: 31px;
    height: 31px;
    flex: 0 0 auto;
    border-radius: 9px;
    background: #ffffff;
    color: #047857;
  }

  .shop-image-tip strong {
    display: block;
    margin-bottom: 2px;
    color: #475569;
    font-size: 10px;
  }

  .shop-image-tip span {
    display: block;
    color: #7d8a84;
    font-size: 9px;
    line-height: 1.5;
  }

  /* ========================================================
     ACTIONS
  ======================================================== */

  .shop-image-actions {
    display: flex;
    align-items: center;
    justify-content:
      space-between;
    gap: 20px;
    margin-top: 18px;
    padding: 17px 18px;
    border: 1px solid #e5e7eb;
    border-radius: 15px;
    background: #ffffff;
  }

  .shop-image-action-copy strong {
    display: block;
    margin-bottom: 3px;
    color: #334155;
    font-size: 10px;
  }

  .shop-image-action-copy span {
    display: block;
    color: #94a3b8;
    font-size: 9px;
    line-height: 1.45;
  }

  .shop-image-save-button {
    display: inline-flex;
    min-width: 145px;
    min-height: 40px;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 9px 14px;
    border: none;
    border-radius: 10px;
    background: #047857;
    color: #ffffff;
    cursor: pointer;
    font-family: inherit;
    font-size: 10px;
    font-weight: 800;
    box-shadow:
      0 7px 18px
      rgba(
        4,
        120,
        87,
        0.16
      );
  }

  .shop-image-save-button:hover:not(:disabled) {
    background: #036b4f;
  }

  .shop-image-save-button:disabled,
  .shop-image-clear-button:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  .shop-image-button-spinner {
    width: 11px;
    height: 11px;
    border: 2px solid
      rgba(
        255,
        255,
        255,
        0.4
      );
    border-top-color: #ffffff;
    border-radius: 50%;
    animation:
      shop-image-spin
      0.7s
      linear
      infinite;
  }

  @keyframes shop-image-spin {
    to {
      transform: rotate(360deg);
    }
  }

  /* ========================================================
     RESPONSIVE
  ======================================================== */

  @media (max-width: 820px) {

    .shop-image-layout {
      grid-template-columns: 1fr;
    }

  }

  @media (max-width: 560px) {

    .shop-image-panel,
    .shop-image-preview-panel {
      padding: 17px;
      border-radius: 17px;
    }

    .shop-image-actions {
      align-items: stretch;
      flex-direction: column;
    }

    .shop-image-save-button {
      width: 100%;
    }

    .shop-image-preview-frame,
    .shop-image-preview-frame img {
      height: 240px;
      min-height: 240px;
    }

  }

`;